import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { CreateChancel, Input, NotError, SelectAutocompleteMultiple } from 'components';
import { goalTypes, TAModes, teachingMethodEnums, teachingMethods } from 'fragments';
import { domainActions, libraryActions, httpRequestsOnSuccessActions, httpRequestsOnErrorsActions } from 'store';
import {FindLoad, useModal, FindSuccessItem, goalDataTypes, FindErrorItem, goalDataTypeEnums, goalPerRateEnumList, Images} from 'utils';

export const AddGoal = ({ info, isClone, requestParams, afterSuccess }) => {
    const domainList = useSelector((state) => state.domains.domainList);
    const ACTION_TYPE = isClone ? 'CLONE_LIBRARY_GOAL' : info ? 'EDIT_LIBRARY_GOAL' : 'CREATE_LIBRARY_GOAL';
    const dispatch = useDispatch();
    const success = FindSuccessItem(ACTION_TYPE);
    const beckError = FindErrorItem(ACTION_TYPE);
    const loader = FindLoad(ACTION_TYPE);
    const { close } = useModal();
    const { handleSubmit, control, watch, reset, setValue } = useForm({
        defaultValues: {
            minTrials: 1,
        },
    });
    const dataTypeLocked = info?.id && !isClone;
    const watchedDataType = watch('dataType');
    const dataTypeOptions =
        isClone && info?.dataType !== goalDataTypeEnums.RATE
            ? goalDataTypes.filter((option) => option.id !== goalDataTypeEnums.RATE)
            : goalDataTypes;

    useEffect(() => {
        dispatch(domainActions.getDomains());
    }, []);

    useEffect(() => {
        if (beckError?.error === 'Goal conflict: coincident record found') {
            toast(<NotError type={'error'} errMessage={'Goal Name must be unique.'} />);
            dispatch(httpRequestsOnErrorsActions.removeError(ACTION_TYPE));
        }
    }, [beckError]);

    useEffect(() => {
        if (info) {
            const params = {
                ...info,
                description: info?.description || '',
                maxTrials: info?.maxTrials ?? '',
            };

            info?.domain ? (params.domain = info?.domain?.id) : delete params.domain;
            reset(params);
        }
    }, [info]);

    useEffect(() => {
        if (success) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
            close();

            if (success?.data && afterSuccess) {
                afterSuccess(success?.data);
            }
        }
    }, [success]);

    const handleCreate = (data) => {
        const params = {
            ...data,
            description: data.description,
        };

        data?.minTrials ? (params.minTrials = +data.minTrials) : delete params.minTrials;
        // Empty max = no limit: omitted on create, `null` on edit so an existing limit is removed.
        const hasMax = data?.maxTrials !== '' && data?.maxTrials != null;
        if (hasMax) params.maxTrials = +data.maxTrials;
        else if (info && !isClone) params.maxTrials = null;
        else delete params.maxTrials;
        // Empty teaching method: omitted on create, `null` on edit so an existing one is removed.
        if (!data?.teachingMethod) {
            if (info && !isClone) params.teachingMethod = null;
            else delete params.teachingMethod;
        }
        if (data?.dataType !== goalDataTypeEnums.RATE || !data?.timeUnit) {
            delete params.timeUnit;
        }

        if (isClone) {
            const clonePayload = { };
            if(data?.dataType !== info?.dataType){
                clonePayload.dataType = data?.dataType;
            }
            if(data?.name !==info?.name){
                clonePayload.name = data?.name;
            }
            dispatch(libraryActions.cloneLibraryGoal(info.id, clonePayload, requestParams));
        } else if (info) {
            dispatch(libraryActions.editLibraryGoal(info.id, params, requestParams));
        } else {
            dispatch(libraryActions.createLibraryGoal(params, requestParams));
        }
    };

    return (
        <div style={{ width: 1000, maxWidth: '100%' }}>
            <p className="modal-header-title">{isClone ? 'Duplicate Goal' : info ? 'Edit Goal' : 'Add New Goal'}</p>
            <form onSubmit={handleSubmit(handleCreate)}>
                <div className="modal-body-wrapper">
                    <div className="flex-align-start" style={{ gap: 16 }}>
                        <div className="full-width">
                            <Input
                                name="name"
                                label="Goal Name*"
                                variant="outlined"
                                control={control}
                                // maxLength={50}
                                rules={{
                                    // maxLength: {
                                    //     value: 50,
                                    //     message: 'Max length is 50 digits.',
                                    // },
                                    required: 'Goal name is required.',
                                    validate: (value) => value.trim().length > 0 || 'Goal name cannot be empty spaces.',
                                }}
                            />
                            <SelectAutocompleteMultiple
                                name={`teachingMethod`}
                                label={'Teaching Method'}
                                control={control}
                                options={teachingMethods}
                                renderValue={(option) => option?.label}
                            />
                            <SelectAutocompleteMultiple
                                loadType={'GET_DOMAINS'}
                                name={`domain`}
                                label={'Domain'}
                                control={control}
                                options={domainList}
                                renderValue={(option) => option?.name}
                            />

                            {watch('teachingMethod') === teachingMethodEnums.TASK_ANALYSIS && (
                                <SelectAutocompleteMultiple
                                    name={`TAMode`}
                                    label={'Chaining*'}
                                    control={control}
                                    options={TAModes}
                                    rules={{ required: 'Mode is required.' }}
                                    renderValue={(option) => option?.label}
                                />
                            )}
                        </div>

                        <div className="full-width">
                            <SelectAutocompleteMultiple
                                name={`type`}
                                label={'Type*'}
                                control={control}
                                options={goalTypes}
                                rules={{ required: 'Type is required.' }}
                                renderValue={(option) => option?.label}
                            />

                            <div className="flex-align-center" style={{ gap: 12 }}>
                                <SelectAutocompleteMultiple
                                    name={`dataType`}
                                    label={'Data Collection Method*'}
                                    control={control}
                                    options={dataTypeOptions}
                                    rules={{ required: 'Mode is required.' }}
                                    renderValue={(option) => option?.label}
                                    disabled={dataTypeLocked}
                                    handleChange={() => setValue('timeUnit', null)}
                                />
                                {(dataTypeLocked ? info?.dataType === goalDataTypeEnums.RATE : watchedDataType === goalDataTypeEnums.RATE) && (
                                    <SelectAutocompleteMultiple
                                        name={'timeUnit'}
                                        label={'Rate Measured Per*'}
                                        control={control}
                                        options={goalPerRateEnumList}
                                        rules={{ required: 'Rate Measured is required.' }}
                                        renderValue={(option) => option?.label}
                                        disabled={isClone}
                                    />
                                )}
                            </div>


                            <div className="flex-align-start" style={{ gap: 12 }}>
                                <Input
                                    type="number"
                                    name="minTrials"
                                    label="Min Trials*"
                                    variant="outlined"
                                    control={control}
                                    rules={{
                                        required: 'Min trials is required.',
                                        min: {
                                            value: 0,
                                            message: 'Min trials cannot be negative.',
                                        },
                                    }}
                                    min={0}
                                />
                                {/* Hard cap per session — empty means no limit. */}
                                <Input
                                    type="number"
                                    name="maxTrials"
                                    label="Max Trials"
                                    variant="outlined"
                                    control={control}
                                    rules={{
                                        validate: (value) => {
                                            if (value === '' || value === null || value === undefined) return true;
                                            if (!Number.isInteger(+value) || +value < 1) return 'Max trials must be a whole number of at least 1.';
                                            const min = watch('minTrials');
                                            if (min !== '' && min != null && +value < +min) return 'Max trials cannot be less than min trials.';
                                            return true;
                                        },
                                    }}
                                    min={1}
                                />
                            </div>
                        </div>
                    </div>

                    {isClone && (
                        <div className="clone-goal-info-box">
                            <img src={Images.infoCircle} alt="info" />
                            <span>
                                All goal settings and{' '}
                                <b>
                                    {info?.targets?.length ?? 0} {(info?.targets?.length ?? 0) === 1 ? 'target' : 'targets'}
                                </b>{' '}
                                from the original goal will be copied to the new goal.
                            </span>
                        </div>
                    )}
                    {isClone && watchedDataType !== info?.dataType && (
                        <div className='changing-goal-method-box'>
                            <img src={Images.alertWarning} alt='warning'/>
                            <span>Changing this method will reset related options.</span>
                        </div>
                    )}
                </div>
                <CreateChancel
                    type="submit"
                    loader={!!loader.length}
                    create={isClone ? 'Duplicate' : info ? 'Save' : 'Add'}
                    chancel={'Cancel'}
                    onClose={close}
                    buttonWidth="100%"
                    cancelRight={'24px'}
                />
            </form>
        </div>
    );
};
